import emit from '../../common/events';
import environment from '../../common/environment';
import { ariaLabelWithNewTab } from '../../common/utils/a11y-util';
import store from '../../store/store';
import { StoreState } from '../../store/store.interface';
import { getCart } from '../cart/shop-cart/store/shop-cart.selectors';
import { getOrderApplicationForAnalytics } from '../../common/utils/cart-util';
import { isTunnelPurchaseFlow } from '../../common/utils/util';
import { BaseComponent } from '../base-component/base-component';
import { booleanConverter } from '../../common/converters';
import { css, html, nothing, TemplateResult } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { ALEKS_SHARPEN_COMPANION_LAUNCH_URL, GO_COMPANION_LAUNCH_URL, SHARPEN_ADVANTAGE_LAUNCH_URL, SHARPEN_COMPANION_LAUNCH_URL } from '../upsell/constants/sharpen-page-type.constants';
import { getCartAnalyticsOverrides } from '../cart/shop-cart/shop-cart.analytics';

@customElement('mhecomm-sharpen-legal-disclaimer')
export class SharpenLegalDisclaimer extends BaseComponent {
  static styles = css`
    .alert.border-custom {
      border: 2px solid var(--theme-cyan-200);
    }

    .small-text {
      font-size: 12px;
    }
  `;

  @property({ converter: booleanConverter })
  isConnectSharpenBundle?: boolean;

  @property({ converter: booleanConverter })
  isOrderConfirmation?: boolean;

  @property({ converter: booleanConverter })
  isSharpenCompanion?: boolean;

  @property({ converter: booleanConverter })
  isSharpenAdvantage?: boolean;

  @property({ converter: booleanConverter })
  isMhGoItem?: boolean;

  @property({ converter: booleanConverter })
  isSharpenPlus?: boolean;

  @property({ converter: booleanConverter })
  isAleksSharpenCompanionBundle?: boolean;

  @property({ converter: booleanConverter })
  plainSharpenLegalDisclaimer?: boolean;

  @property()
  sharpenLegalDisclaimerPrefix?: string;

  @property()
  analyticsApplication?: string;

  @property()
  analyticsOrderType?: string;

  renderComp() {
    if (this.isSharpenPlus) {
      return this.displaySharpenLegalDisclaimer();
    }

    if (!this.isConnectSharpenBundle && !(this.isSharpenCompanion || this.isSharpenAdvantage)) {
      return nothing;
    }

    if (this.isSharpenCompanion && this.isOrderConfirmation && !this.isAleksSharpenCompanionBundle) {
      return this.getSharpenExamPrepDisclaimer();
    }

    if (this.isSharpenAdvantage) {
      return this.getSharpenAdvantageStudyAppDisclaimer();
    }

    if (this.isAleksSharpenCompanionBundle && this.isOrderConfirmation) {
      return this.getAleksSharpenCompanionDisclaimer();
    }

    return this.getSharpenStudyAppDisclaimer();
  }

  /**
   * Displays the Sharpen exam prep disclaimer (Sharpen companion order confirmation).
   *
   * @private
   */
  private getSharpenExamPrepDisclaimer(): TemplateResult {
    return html`
      <div data-automation-id="sharpen-exam-prep-verbiage">
        <h3 class="fw-bold fs-6 p-0 m-0" data-automation-id="sharpen-exam-prep-title">Sharpen Exam Prep App:</div>
      <p data-automation-id="sharpen-exam-prep-text" class="fs-small">
        Access to the Sharpen app will be sent to you via email.
        <a
          href="${this.determineSharpenLaunchUrl()}"
          target="_blank"
          data-automation-id="sharpen-exam-prep-link"
          aria-label="${ariaLabelWithNewTab('Click here to sign in')}"
          @click=${() => this.fireAnalytics('Sharpen Sign In')}
        >Click here to sign in.
        </a>
      </p>

      ${this.isPaymentStep() ? this.displaySharpenLegalDisclaimer() : ``}
      </div>
    `;
  }

  /**
   * Displays the Sharpen study app disclaimer (Sharpen advantage order confirmation).
   *
   * @private
   */
  private getSharpenAdvantageStudyAppDisclaimer(): TemplateResult {
    return html`
      <div data-automation-id="sharpen-study-app-verbiage">
        <h3 class="fw-bold fs-6 p-0 m-0" data-automation-id="sharpen-study-app-title">Sharpen Study App:</div>
      <p data-automation-id="sharpen-exam-prep-text" class="fs-small">
        Access to the sharpen app will be sent to you via email.
        ${
          this.isOrderConfirmation
            ? html` <br />
                <a
                  href="${this.determineSharpenLaunchUrl()}"
                  target="_blank"
                  data-automation-id="sharpen-study-app-link"
                  aria-label="Click here to access the Sharpen Study App"
                  @click=${() => this.fireAnalytics('Sharpen Study App Sign In')}
                  >Click here
                </a>`
            : nothing
        }
      </p>
      ${this.isPaymentStep() ? this.displaySharpenLegalDisclaimer() : ``}
      </div>
    `;
  }

  /**
   * Displays the Sharpen Study App disclaimer (every Sharpen bundle except Sharpen companion order confirmation).
   */
  private getSharpenStudyAppDisclaimer(): TemplateResult {
    return html`
      <div data-automation-id="sharpen-study-app-verbiage">
        <h3 class="fw-bold fs-6 p-0 m-0" data-automation-id="sharpen-study-app-title">Sharpen Study App:</h3>
        <p data-automation-id="sharpen-study-app-text" class="fs-small">
          Access to the Sharpen app will be sent to you via email.
        </p>
        ${this.isPaymentStep() ? this.displaySharpenLegalDisclaimer() : ``}
      </div>
    `;
  }

  /**
   * Displays the legal disclaimer for Sharpen.
   *
   * When {@link plainSharpenLegalDisclaimer} is true, returns a plain div without the info alert styling.
   */
  private displaySharpenLegalDisclaimer(): TemplateResult {
    if (this.plainSharpenLegalDisclaimer) {
      return html`<div data-automation-id="sharpen-study-app-disclaimer">${this.getDisclaimerContent()}</div>`;
    }

    return html` <div
      class="mt-3 alert alert-info border-custom pb-0"
      data-automation-id="sharpen-study-app-disclaimer">
      <p>${this.getDisclaimerContent()}</p>
    </div>`;
  }

  private getDisclaimerContent(): TemplateResult {
    return html`
      ${this.isSharpenCompanion || this.isSharpenPlus || this.isSharpenAdvantage || this.isConnectSharpenBundle
        ? this.getSharpenCompanionDisclaimer()
        : ''}
      For questions, see our
      <a
        href="${environment().returnPolicyUrl}"
        target="_blank"
        data-automation-id="return-policy"
        aria-label="${ariaLabelWithNewTab('Return Policy')}"
        class="text-decoration-underline"
        @click=${() => this.fireAnalytics('Return Policy')}
      >
        <strong>Return Policy</strong>
      </a>
      or
      <a
        target="_blank"
        aria-label="${ariaLabelWithNewTab('email us')}"
        href="${environment().sharpenUsMailto}"
        data-automation-id="sharpen-us-mailto"
        class="text-decoration-underline"
        @click=${() => this.fireAnalytics('Email Us')}
      >
        <strong>email us</strong>
      </a>.
    `;
  }

  private getSharpenCompanionDisclaimer(): TemplateResult {
    return html`${this.sharpenLegalDisclaimerPrefix}No partial refunds available for existing Sharpen entitlements or
    subscriptions. Sharpen is available only for users located in the US, US territories, and India.`;
  }

  private isPaymentStep(): boolean {
    return store.getState().shopCheckoutState?.activeStepId === 'payment-billing-checkout-step';
  }

  private getAleksSharpenCompanionDisclaimer(): TemplateResult {
    return html`
      <div data-automation-id="sharpen-study-app-verbiage">
        <strong>Sharpen Study App:</strong>
        <p>Sharpen access instructions will be emailed to you.
          First, click Sharpen's launch point in ALEKS to set up your Sharpen account.
          Then you'll be able to log in with your university email at
          <a
            href="${this.determineSharpenLaunchUrl()}"
            target="_blank"
            data-automation-id="sharpen-study-app-link"
            aria-label="Click here to access the Sharpen Study App"
            @click=${() => this.fireAnalytics('go-to-sharpen-order-confirmation')}
            >studysharpen.com
          </a>
           or in the mobile phone app.
        </p>
      </div>
    `
  }

  private fireAnalytics(eventName: string): void {
    const state = store.getState() as StoreState;
    const cart = getCart(state);
    const isTunnel = isTunnelPurchaseFlow();
    const analyticsOverrides = getCartAnalyticsOverrides(state);
    const injectedApplication = this.analyticsApplication?.trim();
    const injectedOrderType = this.analyticsOrderType?.trim();
    const fallbackApplication =
      isTunnel && cart?.products[0]
        ? getOrderApplicationForAnalytics(cart.products[0].productMetadata!)
        : undefined;

    emit({
      element: this,
      name: 'click',
      detail: {
        comp: 'sharpen-legal-disclaimer',
        eventName: eventName.toLowerCase().replace(/\s/g, '-'),
        label: eventName,
        url: window.location.href,
        ...analyticsOverrides,
        ...(injectedOrderType ? { orderType: injectedOrderType } : {}),
        flow: isTunnel ? 'tunnel-purchase' : 'mcgraw',
        ...(injectedApplication || fallbackApplication
          ? {
              application: injectedApplication || fallbackApplication,
            }
          : {}),
      },
    });
  }

  private determineSharpenLaunchUrl() {
    switch (true) {
      case !!this.isMhGoItem:
        return GO_COMPANION_LAUNCH_URL;
      case !!this.isAleksSharpenCompanionBundle:
        return ALEKS_SHARPEN_COMPANION_LAUNCH_URL;
      case !!this.isSharpenCompanion:
        return SHARPEN_COMPANION_LAUNCH_URL;
      case !!this.isSharpenAdvantage:
        return SHARPEN_ADVANTAGE_LAUNCH_URL;
      default:
        return '';
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-sharpen-legal-disclaimer': SharpenLegalDisclaimer;
  }
}
